export type FedExPackagingCategory = 'ONE_RATE_DOMESTIC' | 'INTERNATIONAL_FIXED';

export type FedExPackagingId =
  | 'YOUR_PACKAGING'
  | 'FEDEX_EXTRA_SMALL_BOX'
  | 'FEDEX_SMALL_BOX'
  | 'FEDEX_MEDIUM_BOX'
  | 'FEDEX_LARGE_BOX'
  | 'FEDEX_EXTRA_LARGE_BOX'
  | 'FEDEX_TUBE'
  | 'FEDEX_PAK'
  | 'FEDEX_ENVELOPE'
  | 'FEDEX_10KG_BOX'
  | 'FEDEX_25KG_BOX'
  | 'FEDEX_BOX';

export type FedExPackagingOption = {
  id: FedExPackagingId;
  label: string;
  category: FedExPackagingCategory;
  lengthIn: number;
  widthIn: number;
  heightIn: number;
  maxWeightLbs: number;
  dimensionsLabel: string;
  hint: string;
};

function dimLabel(l: number, w: number, h: number): string {
  const fmt = (n: number) => {
    const whole = Math.floor(n);
    const frac = n - whole;
    if (frac < 0.01) return `${whole}"`;
    if (Math.abs(frac - 0.375) < 0.02) return `${whole} 3/8"`;
    if (Math.abs(frac - 0.5) < 0.02) return `${whole} 1/2"`;
    if (Math.abs(frac - 0.6875) < 0.02) return `${whole} 11/16"`;
    if (Math.abs(frac - 0.25) < 0.02) return `${whole} 1/4"`;
    return `${n.toFixed(2)}"`;
  };
  return `${fmt(l)} × ${fmt(w)} × ${fmt(h)}`;
}

export const FEDEX_ONE_RATE_PACKAGING: FedExPackagingOption[] = [
  {
    id: 'FEDEX_EXTRA_SMALL_BOX',
    label: 'Extra Small Box',
    category: 'ONE_RATE_DOMESTIC',
    lengthIn: 7.25,
    widthIn: 2.5,
    heightIn: 4.6875,
    maxWeightLbs: 50,
    dimensionsLabel: dimLabel(7.25, 2.5, 4.6875),
    hint: 'One Rate · up to 50 lb',
  },
  {
    id: 'FEDEX_SMALL_BOX',
    label: 'Small Box',
    category: 'ONE_RATE_DOMESTIC',
    lengthIn: 10.875,
    widthIn: 1.5,
    heightIn: 12.375,
    maxWeightLbs: 50,
    dimensionsLabel: dimLabel(10.875, 1.5, 12.375),
    hint: 'One Rate · up to 50 lb',
  },
  {
    id: 'FEDEX_MEDIUM_BOX',
    label: 'Medium Box',
    category: 'ONE_RATE_DOMESTIC',
    lengthIn: 11.5,
    widthIn: 2.375,
    heightIn: 13.25,
    maxWeightLbs: 50,
    dimensionsLabel: dimLabel(11.5, 2.375, 13.25),
    hint: 'One Rate · up to 50 lb',
  },
  {
    id: 'FEDEX_LARGE_BOX',
    label: 'Large Box',
    category: 'ONE_RATE_DOMESTIC',
    lengthIn: 12.375,
    widthIn: 3,
    heightIn: 17.5,
    maxWeightLbs: 50,
    dimensionsLabel: dimLabel(12.375, 3, 17.5),
    hint: 'One Rate · up to 50 lb',
  },
  {
    id: 'FEDEX_EXTRA_LARGE_BOX',
    label: 'Extra Large Box',
    category: 'ONE_RATE_DOMESTIC',
    lengthIn: 11.875,
    widthIn: 10.75,
    heightIn: 11,
    maxWeightLbs: 50,
    dimensionsLabel: dimLabel(11.875, 10.75, 11),
    hint: 'One Rate · up to 50 lb',
  },
  {
    id: 'FEDEX_TUBE',
    label: 'Tube',
    category: 'ONE_RATE_DOMESTIC',
    lengthIn: 6,
    widthIn: 6,
    heightIn: 38,
    maxWeightLbs: 50,
    dimensionsLabel: dimLabel(6, 6, 38),
    hint: 'Blueprints & posters',
  },
  {
    id: 'FEDEX_PAK',
    label: 'FedEx Pak',
    category: 'ONE_RATE_DOMESTIC',
    lengthIn: 12,
    widthIn: 9.5,
    heightIn: 0.5,
    maxWeightLbs: 50,
    dimensionsLabel: '12" × 9 1/2" pak',
    hint: 'Flat documents & soft goods',
  },
  {
    id: 'FEDEX_ENVELOPE',
    label: 'FedEx Envelope',
    category: 'ONE_RATE_DOMESTIC',
    lengthIn: 9.5,
    widthIn: 12.5,
    heightIn: 0.25,
    maxWeightLbs: 50,
    dimensionsLabel: '9 1/2" × 12 1/2" envelope',
    hint: 'Documents only',
  },
];

export const FEDEX_INTERNATIONAL_PACKAGING: FedExPackagingOption[] = [
  {
    id: 'FEDEX_10KG_BOX',
    label: 'International 10 kg Box',
    category: 'INTERNATIONAL_FIXED',
    lengthIn: 15.8,
    widthIn: 12.9,
    heightIn: 10.2,
    maxWeightLbs: 22,
    dimensionsLabel: '≈ 15.8" × 12.9" × 10.2"',
    hint: 'Fixed rate · max 10 kg (22 lb)',
  },
  {
    id: 'FEDEX_25KG_BOX',
    label: 'International 25 kg Box',
    category: 'INTERNATIONAL_FIXED',
    lengthIn: 21.6,
    widthIn: 16.6,
    heightIn: 13.2,
    maxWeightLbs: 55,
    dimensionsLabel: '≈ 21.6" × 16.6" × 13.2"',
    hint: 'Fixed rate · max 25 kg (55 lb)',
  },
];

export const DEFAULT_FEDEX_PACKAGING_ID: FedExPackagingId = 'FEDEX_MEDIUM_BOX';

export function getFedExPackagingOption(id: FedExPackagingId): FedExPackagingOption | undefined {
  if (id === 'FEDEX_BOX') {
    return FEDEX_ONE_RATE_PACKAGING.find((o) => o.id === 'FEDEX_MEDIUM_BOX');
  }
  return (
    FEDEX_ONE_RATE_PACKAGING.find((o) => o.id === id) ||
    FEDEX_INTERNATIONAL_PACKAGING.find((o) => o.id === id)
  );
}

export function packagingOptionsForDestination(isInternational: boolean): FedExPackagingOption[] {
  if (isInternational) {
    return [...FEDEX_INTERNATIONAL_PACKAGING, ...FEDEX_ONE_RATE_PACKAGING];
  }
  return FEDEX_ONE_RATE_PACKAGING;
}
